// src/pages/ToolsDashboard.js
import React, { useState, useMemo, useEffect, useRef } from 'react'
import {
  Key,
  QrCode,
  Ruler,
  GitBranch,
  Search,
  MonitorPlay,
  Hammer,
  LayoutGrid,
  ArrowUpRight,
  Settings,
  Terminal,
  Globe,
  ShieldCheck,
  Cpu,
  RefreshCw,
  Calculator,
  Sparkles,
  Wrench,
  ChevronRight,
  X,
  Image,
  FileText,
  Briefcase,
  ArrowRight,
  Code,
  Repeat,
  Check,
  RotateCcw,
  Zap,
  Clock,
  Menu,
  DollarSign,
  CreditCard,
  Activity,
  Calendar,
  Type,
  Palette,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import OmkarEngineHeader from '../components/omkarheader'
import {toolsData} from "../Data/ToolsData"



export default function ToolsDashboard() {
  const [searchTerm, setSearchTerm] = useState('')
  const [activeCategory, setActiveCategory] = useState('All')
  const [moreOpen, setMoreOpen] = useState(false)
const [time, setTime] = useState(new Date());
  const [isLive, setIsLive] = useState(true);
  const [systemStatus, setSystemStatus] = useState('Stable');
  const [orbitSpeed, setOrbitSpeed] = useState(1);
  const [colors, setColors] = useState(['#6366f1', '#a855f7', '#ec4899']);
const [gain, setGain] = useState(84);

const gridRef = useRef(null);
const [isGridVisible, setIsGridVisible] = useState(false);

useEffect(() => {
  const observer = new IntersectionObserver(
    ([entry]) => {
      // Logic: If the grid is visible, show nav. If not, hide it.
      setIsGridVisible(entry.isIntersecting);
    },
    { 
      // 0.1 means if 10% of the grid is visible, the nav pops up.
      threshold: 0.1, 
      // rootMargin helps trigger the nav slightly before/after the section
      rootMargin: "0px 0px -50px 0px" 
    }
  );

  if (gridRef.current) {
    observer.observe(gridRef.current);
  }

  return () => observer.disconnect();
}, []);

  // Update Clock every second
  useEffect(() => {
    const timer = setInterval(() => {
      if (isLive) setTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, [isLive]);

  const togglePulse = () => {
    setIsLive(!isLive);
    setSystemStatus(!isLive ? 'Stable' : 'Paused');
  };

  const handleTurbo = () => {
    setOrbitSpeed(prev => (prev === 1 ? 5 : 1));
    setSystemStatus(orbitSpeed === 1 ? 'Turbo' : 'Stable');
  };

const filteredTools = useMemo(() => {
  return toolsData.filter((t) => {
    // 1. Category/Title Match
    const matchesCategory = 
      activeCategory === 'All' || 
      t.category === activeCategory || 
      t.title === activeCategory; // Add this to catch specific tool clicks

    // 2. Search Term Match
    const matchesSearch = t.title.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesCategory && matchesSearch;
  });
}, [activeCategory, searchTerm]);

  // 1. Define an icon mapping for categories
const categoryIcons = {
  "All": <LayoutGrid size={20} />,
  "IT Tools": <ShieldCheck size={20} />,
  "NDT Tools": <Cpu size={20} />,
  "Entertainment": <MonitorPlay size={20} />,
  "Security Tools": <Wrench size={20} />
};

// 2. Extract unique categories from toolsData
const uniqueCategories = ["All", ...new Set(toolsData.map(tool => tool.category))];

// 3. Create the format for your Nav and Sidebar
const menuItems = uniqueCategories.map(cat => ({
  label: cat,
  icon: categoryIcons[cat] || <Wrench size={20} /> // Fallback icon
}));

// 4. Create the nested structure for the "More Drawer" (grouped by category)
const menuStructure = uniqueCategories.filter(cat => cat !== "All").map(cat => ({
  category: cat,
  items: toolsData
    .filter(tool => tool.category === cat)
    .map(tool => ({ label: tool.title, icon: tool.icon }))
}));



  const MAX_VISIBLE = 4

  const visibleMenus = menuItems.slice(0, MAX_VISIBLE)
  const hiddenMenus = menuItems.slice(MAX_VISIBLE)

  const scrollToGrid = () => {
    const section = document.getElementById('tools-grid')
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }



  // 2. Handlers
  const randomizeColors = () => {
    const newColors = colors.map(() => `#${Math.floor(Math.random()*16777215).toString(16)}`);
    setColors(newColors);
  };

  const handleGainClick = () => {
    setGain(Math.floor(Math.random() * 100));
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 overflow-x-hidden selection:bg-indigo-600 selection:text-white pb-32">
      <Helmet>
        <title>
          Industrial Toolset & NDT Engineering Dashboard | Omkar More
        </title>
        <meta
          name="description"
          content="Professional suite of NDT calculators, IT utilities, and industrial tools. Streamlined engineering modules for precision work."
        />
      </Helmet>

      <main className="relative w-full min-h-screen px-6 md:px-10 lg:px-14 py-12">
        {/* HEADER */}
        <OmkarEngineHeader />


        {/* TOOL GRID */}
{/* TOOL GRID */}
{/* TOOL GRID */}
<div ref={gridRef} className="max-w-7xl mx-auto px-4 py-12">  
  {/* --- INTRO SECTION --- */}
{/* --- INTRO SECTION --- */}
<header className="mb-12 max-w-3xl">
  {/* Tagline */}
  <div className="flex items-center gap-2 text-cyan-600 mb-3">
    <div className="h-1 w-12 bg-cyan-600 rounded-full" />
    <span className="text-xs font-black uppercase tracking-[0.2em]">
      OM Tool Hub
    </span>
  </div>

  {/* Main Headline */}
  <h1 className="text-4xl font-bold text-slate-900 mb-4 tracking-tight">
    Professional Online <br />
    <span className="text-cyan-600">Calculators & Utilities</span>
  </h1>

  {/* Description */}
  <p className="text-lg text-slate-600 leading-relaxed">
    Access a comprehensive collection of online calculators, utilities, and reference tools 
    designed for professionals across various domains.  
    OM Tool Hub provides reliable, easy-to-use resources to simplify calculations, improve workflow, 
    and enhance productivity for engineers, developers, and technical experts.
  </p>

  {/* SEO Keywords (hidden for crawlers) */}
  <span className="sr-only">
    Free online calculators, utilities, professional tools, engineering calculators, developer utilities, workflow resources, productivity tools, OM Tool Hub.
  </span>
</header>

  {/* --- TOOL GRID --- */}
<section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">    {filteredTools.map((tool) => (
      <article
        key={tool.id}
        className="group bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-xl hover:border-cyan-500/50 transition-all duration-300 overflow-hidden flex flex-col"
      >
        <Link to={tool.url} className="flex flex-col h-full">
          
          {/* IMAGE AREA */}
          <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
            <img
              src={tool.image}
              alt={tool.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute top-3 left-3">
              <span className="px-2 py-1 bg-slate-900/80 text-white text-[10px] font-bold uppercase tracking-wider rounded">
                {tool.category}
              </span>
            </div>
          </div>

          {/* TEXT CONTENT */}
          <div className="p-6 flex flex-col flex-grow">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-cyan-500" />
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                  System Ready
                </span>
              </div>
              <div className="text-slate-300 group-hover:text-cyan-600 transition-colors">
                <ArrowUpRight size={20} />
              </div>
            </div>

            <h2 className="text-xl font-bold text-slate-800 mb-2">
              {tool.title}
            </h2>

            <p className="text-sm text-slate-500 leading-relaxed line-clamp-2">
              {tool.description}
            </p>

            {/* FOOTER */}
            <div className="mt-auto pt-5 border-t border-slate-50 flex items-center justify-between">
              <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">
                ISO Compliance
              </span>
              <span className="text-xs font-bold text-cyan-600 opacity-0 group-hover:opacity-100 transition-all">
                Open Tool →
              </span>
            </div>
          </div>
        </Link>
      </article>
    ))}
  </section>
</div>


      </main>

      {/* BOTTOM NAV */}
<div 
  className={`
    fixed bottom-4 sm:bottom-6 left-0 right-0 px-2 sm:px-6 flex justify-center z-[100]
    transition-all duration-700 cubic-bezier(0.4, 0, 0.2, 1)
    ${isGridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'}
  `}
>      
  <nav className="
    flex items-center justify-between gap-1 p-1.5
    bg-slate-950/90 backdrop-blur-2xl 
    border border-slate-800/50 rounded-full 
    shadow-[0_20px_50px_rgba(0,0,0,0.6)] 
    w-full max-w-[640px]
  ">
    
    {/* Show first 4 menu items */}
    {menuItems.slice(0, 4).map((item) => {
      const active = activeCategory === item.label;

      return (
        <button
          key={item.label}
          onClick={() => setActiveCategory(item.label)}
          className={`
            relative flex items-center justify-center
            py-3 transition-all duration-500 min-w-[44px]
            ${active
              ? 'bg-cyan-600 text-white px-3 sm:px-5 flex-[2] rounded-full shadow-[0_0_20px_rgba(8,145,178,0.4)]'
              : 'text-slate-500 hover:text-slate-200 flex-1 rounded-full'
            }
          `}
        >
          <span className={`transition-transform duration-300 ${active ? 'scale-110' : 'scale-100'}`}>
            {item.icon}
          </span>

          {/* Label: Hidden on very small screens unless active to save space */}
          {active && (
            <span className="ml-2 text-[9px] sm:text-xs font-black uppercase tracking-tight whitespace-nowrap animate-in fade-in zoom-in-95">
              {item.label}
            </span>
          )}

     
        </button>
      );
    })}

    {/* Small separator line */}
    <div className="w-[1px] h-5 bg-slate-800/50 mx-0.5" />

    {/* The 'More' Button */}
    <button
      onClick={() => setMoreOpen(true)}
      className="
        flex items-center justify-center
        flex-1 min-w-[44px] py-3
        text-slate-400 hover:text-cyan-400
        hover:bg-slate-900/50 rounded-full
        transition-all duration-300 active:scale-90
      "
    >
      <div className="relative flex items-center gap-1.5">
        <Menu size={20} />
        
        {/* Tools Count Badge */}
        <span className="hidden xs:block bg-slate-800 text-cyan-500 text-[8px] font-bold px-1.5 py-0.5 rounded-full border border-slate-700">
          {toolsData.length}
        </span>
      </div>
    </button>
  </nav>
</div>

      {/* MORE DRAWER */}
{/* MORE DRAWER */}
<div className={`fixed inset-0 z-[200] transition-all duration-300 ${moreOpen ? 'visible' : 'invisible'}`}>
  {/* Overlay */}
  <div
    onClick={() => setMoreOpen(false)}
    className={`absolute inset-0 bg-slate-950/60 backdrop-blur-md transition-opacity duration-500 ${moreOpen ? 'opacity-100' : 'opacity-0'}`}
  />

  {/* Sidebar Drawer - Full width on mobile, 320px on desktop */}
  <div
    className={`
      absolute right-0 top-0 h-full 
      w-full sm:w-[320px] 
      bg-slate-950 border-l border-slate-800/50
      shadow-[-20px_0_50px_rgba(0,0,0,0.5)]
      p-6 sm:p-8 flex flex-col
      transition-transform duration-500 cubic-bezier(0.4, 0, 0.2, 1)
      ${moreOpen ? 'translate-x-0' : 'translate-x-full'}
    `}
  >
    {/* HEADER SECTION */}
    <div className="flex justify-between items-center mb-8 sm:mb-10">
      <div className="flex flex-col">
        <h3 className="text-2xl font-black text-white tracking-tight">
          More<span className="text-cyan-500">Tools</span>
        </h3>
        <p className="text-[10px] text-slate-500 font-medium uppercase tracking-widest">
          {toolsData.length} modules loaded
        </p>
      </div>

      <button
        onClick={() => setMoreOpen(false)}
        className="p-3 rounded-full bg-slate-900 text-slate-400 active:scale-90 transition-all shadow-lg"
      >
        <X size={24} />
      </button>
    </div>

    {/* SCROLLABLE MENU SECTIONS */}
    <div className="flex-1 overflow-y-auto pr-2 -mr-2 custom-scrollbar touch-pan-y">
      <div className="flex flex-col gap-8 pb-10">
        {menuStructure.map((group) => (
          <div key={group.category} className="group/section">
            <div className="flex items-center gap-3 mb-4">
              <h4 className="text-[11px] font-bold uppercase tracking-[0.25em] text-cyan-600/80">
                {group.category}
              </h4>
              <div className="h-[1px] flex-grow bg-slate-800/50" />
            </div>

            <div className="flex flex-col gap-3 sm:gap-2">
              {group.items.map((item) => {
                const isActive = activeCategory === item.label;
                return (
                  <button
                    key={item.label}
                    onClick={() => {
                      setActiveCategory(item.label);
                      setMoreOpen(false);
                    }}
                    className={`
                      group relative flex items-center gap-5
                      p-5 sm:p-3.5 rounded-2xl text-left text-sm
                      transition-all duration-200 active:scale-[0.97]
                      ${isActive
                        ? 'bg-cyan-600 text-white shadow-xl shadow-cyan-900/30'
                        : 'bg-slate-900/40 text-slate-400 border border-slate-800/30'
                      }
                    `}
                  >
                    {isActive && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1.5 h-8 bg-white rounded-r-full" />
                    )}

                    <span className={`text-xl sm:text-lg ${isActive ? 'text-white' : 'text-cyan-500'}`}>
                      {item.icon}
                    </span>

                    <span className="font-bold tracking-tight uppercase text-white text-base sm:text-sm">
                      {item.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* DRAWER FOOTER */}
    <div className="mt-4 pt-6 border-t border-slate-800/50">
      <div className="bg-slate-900/60 p-5 rounded-3xl border border-slate-800/50">
        <p className="text-xs text-slate-400 font-medium">
          Built by <span className="text-white">Omkar More</span>
        </p>
        <div className="flex items-center justify-between mt-1">
          <p className="text-[10px] text-slate-500">v1.0 • All systems ready</p>
          <div className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse shadow-[0_0_10px_rgba(6,182,212,1)]" />
        </div>
      </div>
    </div>
  </div>
</div>

    </div>
  )
}
